import { useCallback } from 'react';
import { UserDB, Preference } from 'lib/db';

/**
 * 用户偏好设置 Hook
 * 提供对 UserDB preference 表的增删改查操作
 */
const usePreference = () => {
  /**
   * 根据 key 获取偏好设置值
   * @param key 偏好设置的键
   * @returns 偏好设置的值，如果不存在则返回 undefined
   */
  const getPreference = useCallback(async (key: string): Promise<string | undefined> => {
    try {
      if (!UserDB.instance) {
        console.warn('UserDB 未初始化');
        return undefined;
      }
      const preference = await UserDB.instance.preference.get(key);
      return preference?.value;
    } catch (error) {
      console.error('获取偏好设置失败:', error);
      return undefined;
    }
  }, []);

  /**
   * 设置偏好设置（新增或更新）
   * @param key 偏好设置的键
   * @param value 偏好设置的值
   * @returns 是否设置成功
   */
  const setPreference = useCallback(async (key: string, value: string): Promise<boolean> => {
    try {
      if (!UserDB.instance) {
        console.warn('UserDB 未初始化');
        return false;
      }
      await UserDB.instance.preference.put({ key, value });
      return true;
    } catch (error) {
      console.error('设置偏好设置失败:', error);
      return false;
    }
  }, []);

  /**
   * 删除偏好设置
   * @param key 偏好设置的键
   * @returns 是否删除成功
   */
  const deletePreference = useCallback(async (key: string): Promise<boolean> => {
    try {
      if (!UserDB.instance) {
        console.warn('UserDB 未初始化');
        return false;
      }
      await UserDB.instance.preference.delete(key);
      return true;
    } catch (error) {
      console.error('删除偏好设置失败:', error);
      return false;
    }
  }, []);

  /**
   * 获取所有偏好设置
   * @returns 所有偏好设置的数组
   */
  const getAllPreferences = useCallback(async (): Promise<Preference[]> => {
    try {
      if (!UserDB.instance) {
        console.warn('UserDB 未初始化');
        return [];
      }
      return await UserDB.instance.preference.toArray();
    } catch (error) {
      console.error('获取所有偏好设置失败:', error);
      return [];
    }
  }, []);

  /**
   * 检查偏好设置是否存在
   * @param key 偏好设置的键
   * @returns 是否存在
   */
  const hasPreference = useCallback(async (key: string): Promise<boolean> => {
    try {
      if (!UserDB.instance) {
        return false;
      }
      const count = await UserDB.instance.preference.where('key').equals(key).count();
      return count > 0;
    } catch (error) {
      console.error('检查偏好设置失败:', error);
      return false;
    }
  }, []);

  return {
    getPreference,
    setPreference,
    deletePreference,
    getAllPreferences,
    hasPreference,
  };
};

export default usePreference;
